import { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import ModalComp from './ModalComp';
import MonacoEditor from './MonacoEditor';
import type Quill from "quill";
import "quill/dist/quill.snow.css";
import { forwardRef, useImperativeHandle } from "react";

type EditorOptions = 'html' | 'quill' | 'textarea';

type HTMLEditorProps = {
    initialContent: string;
    onChange: (html: string) => void;
    disabled?: boolean;
    showPreview?: boolean;
    label?: ReactNode;
    className?: string;
    style?: CSSProperties;
    defaultEditorType?: EditorOptions;
    editorOptions?: EditorOptions[];
    editorClassName?: string;
};

const HTMLEditor = (props: HTMLEditorProps) => {
    const preview = props.showPreview ?? true;

    const editorOptions: EditorOptions[] = props.editorOptions ?? ['quill', 'html', 'textarea'];
    const defaultEditorType = props.defaultEditorType ?? editorOptions[0];

    const [content, setContent] = useState<string | null>(props.initialContent ?? '');
    const [EditorType, setEditorType] = useState<EditorOptions>(defaultEditorType);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const quillRef = useRef<QuillCompRef>(null);

    // Track whether the change originated externally (prop) or internally (user edit)
    const isExternalUpdate = useRef(false);

    // Sync external initialContent changes into internal state + Quill
    useEffect(() => {
        if (props.initialContent !== content) {
            isExternalUpdate.current = true;
            setContent(props.initialContent ?? '');
            if (EditorType === 'quill') {
                quillRef.current?.setContents(props.initialContent ?? '');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialContent]);

    // Notify parent only on internal (user-driven) changes, not on external syncs
    useEffect(() => {
        if (isExternalUpdate.current) {
            isExternalUpdate.current = false;
            return;
        }
        props.onChange(content ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content]);

    const handleToggle = (type: EditorOptions) => {
        if (type === 'quill') {
            quillRef.current?.setContents(content ?? '');
        }
        setEditorType(type);
    };


    return (
        <>
            <div className={props.className} style={props.style}>
                <div className='flex items-center gap-x-3'>
                    {props.label ?? <label>Content Text Editor</label>}
                    <div className='flex items-center gap-x-3'>
                        {editorOptions.includes('quill') && (
                            <label>
                                <input
                                    className='form-radio'
                                    type="checkbox"
                                    checked={EditorType === 'quill'}
                                    onChange={() => handleToggle('quill')}
                                />
                                <span> Text Editor</span>
                            </label>
                        )}
                        {editorOptions.includes('html') && (
                            <label>
                                <input
                                    className='form-radio'
                                    type="checkbox"
                                    checked={EditorType === 'html'}
                                    onChange={() => handleToggle('html')}
                                />
                                <span> HTML Editor</span>
                            </label>
                        )}
                        {editorOptions.includes('textarea') && (
                            <label>
                                <input
                                    className='form-radio'
                                    type="checkbox"
                                    checked={EditorType === 'textarea'}
                                    onChange={() => handleToggle('textarea')}
                                />
                                <span> Text Area</span>
                            </label>
                        )}
                    </div>
                </div>

                <div className={props.editorClassName ?? 'w-full'}>
                    {/* Quill — always mounted to preserve instance, shown/hidden via CSS */}
                    {editorOptions.includes('quill') && (
                        <div style={{ display: EditorType === 'quill' ? 'block' : 'none' }}>
                            <QuillComp
                                ref={quillRef}
                                initialContent={content ?? ''}
                                onChange={(c) => setContent(c)}
                                disabled={props.disabled}
                                onQuillParseFail={() => setEditorType('html')}
                            />
                            {preview && !!content && (
                                <button
                                    className="btn btn-outline-success btn-sm mt-1"
                                    type="button"
                                    onClick={() => setModalOpen(true)}
                                >
                                    Preview
                                </button>
                            )}
                        </div>
                    )}

                    {editorOptions.includes('html') && (
                        <div style={{ display: EditorType === 'html' ? 'block' : 'none' }}>
                            <MonacoEditor
                                defaultLanguage='html'
                                className='mt-1'
                                height='300px'
                                code={content}
                                setCode={setContent}
                            />
                            {preview && !!content && (
                                <button
                                    className="btn btn-outline-success btn-sm mt-1"
                                    type="button"
                                    onClick={() => setModalOpen(true)}
                                >
                                    Preview
                                </button>
                            )}
                        </div>
                    )}

                    {editorOptions.includes('textarea') && (
                        <div style={{ display: EditorType === 'textarea' ? 'block' : 'none' }}>
                            <textarea
                                value={content ?? ''}
                                onChange={(e) => setContent(e.target.value)}
                                className="form-textarea min-h-[300px]"
                            />
                            {preview && !!content && (
                                <button
                                    className="btn btn-outline-success btn-sm mt-1"
                                    type="button"
                                    onClick={() => setModalOpen(true)}
                                >
                                    Preview
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {preview && (
                <ModalComp
                    closeBtnView
                    onClose={() => setModalOpen(false)}
                    open={modalOpen}
                    sizeClass="md:w-[50%] w-[80%]"
                    title="Preview"
                    zIndex={9999}
                >
                    <iframe
                        key={content} // re-mount iframe when content changes so srcDoc updates reliably
                        srcDoc={content ?? ''}
                        className="w-full border-none custom-scroll max-h-[80vh] lg:max-h-[60vh]"
                        style={{ minHeight: '400px' }}
                        sandbox="allow-same-origin"
                        title="Preview"
                    />
                </ModalComp>
            )}
        </>
    );
};


// ---------------------------------------------------------------------------
// QuillComp
// ---------------------------------------------------------------------------

type QuillCompRef = {
    setContents: (html: string) => void;
};

type QuillCompProps = {
    initialContent: string;
    onChange: (html: string) => void;
    disabled?: boolean;
    onQuillParseFail?: () => void;
};

const QuillComp = forwardRef<QuillCompRef, QuillCompProps>(
    ({ initialContent, onChange, disabled, onQuillParseFail }, ref) => {
        const editorRef = useRef<HTMLDivElement>(null);
        const quillInstanceRef = useRef<Quill | null>(null);

        // Keep a stable ref to onChange so the text-change listener never becomes stale
        const onChangeRef = useRef(onChange);
        useEffect(() => {
            onChangeRef.current = onChange;
        }, [onChange]);

        useImperativeHandle(ref, () => ({
            setContents: (html: string) => {
                const q = quillInstanceRef.current;
                if (!q) return;
                // Pause the text-change listener while we programmatically set content
                q.off('text-change');
                q.clipboard.dangerouslyPasteHTML(html);
                // Re-attach after a tick so the paste-triggered event is swallowed
                setTimeout(() => {
                    q.on('text-change', () => {
                        onChangeRef.current(q.root.innerHTML);
                    });
                }, 0);
            },
        }));

        useEffect(() => {
            // Only initialise once
            if (typeof window === 'undefined' || quillInstanceRef.current) return;

            const loadQuill = async () => {
                const Quill = (await import('quill')).default;

                if (!editorRef.current || quillInstanceRef.current) return;

                const quill = new Quill(editorRef.current, {
                    theme: 'snow',
                    modules: {
                        toolbar: [
                            [{ header: [1, 2, 3, 4, 5, 6, false] }],
                            [{ font: [] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ color: [] }, { background: [] }],
                            [{ script: 'sub' }, { script: 'super' }],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                        ],
                    },
                });

                quillInstanceRef.current = quill;

                // Load initial content without firing onChange
                if (initialContent) {
                    quill.root.innerHTML = initialContent;

                    if (onQuillParseFail !== undefined) {
                        const delta = quill.clipboard.convert({ html: initialContent });
                        if (!delta?.ops?.length) {
                            onQuillParseFail();
                            return;
                        }
                    }
                }

                // Attach listener AFTER setting initial content
                quill.on('text-change', () => {
                    onChangeRef.current(quill.root.innerHTML);
                });
            };

            loadQuill();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []); // Empty deps — run once on mount only

        useEffect(() => {
            quillInstanceRef.current?.enable(!disabled);
        }, [disabled]);

        return (
            <div className="rounded-xl my-2 h-[350px] md:h-[320px] lg:h-[290px] bg-slate-50 dark:bg-slate-950">
                <div ref={editorRef} style={{ height: '250px' }} />
            </div>
        );
    }
);

QuillComp.displayName = 'QuillEditor';

export default HTMLEditor;